/* BIOVAQ product-banner slideshow (sections/biovaq-banner-slider.liquid): fade autoplay, dots, swipe, keys, pause.
   Same behaviour as assets/biovaq-hero.js, kept separate so the live hero is never touched by banner changes. */
if (!customElements.get('biovaq-banner-slider')) {
  class BiovaqBannerSlider extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.biovaq-banners__slide'));
      if (this.slides.length < 2) return;

      this.dots = Array.from(this.querySelectorAll('.biovaq-banners__dot'));
      this.pauseButton = this.querySelector('[data-pause]');
      this.index = 0;
      this.interval = parseInt(this.dataset.interval, 10) || 5000;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.userPaused = this.dataset.autoplay !== 'true' || reduceMotion;
      this.hovered = false;
      this.focused = false;
      this.editorHold = false;

      this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.go))));
      if (this.pauseButton) {
        this.pauseButton.addEventListener('click', () => {
          this.userPaused = !this.userPaused;
          this.updatePauseButton();
          this.schedule();
        });
      }

      // Pause only for a real mouse hover and for keyboard focus. A tap on a phone fires an emulated mouseenter (and
      // Android, like a desktop click, focuses the tapped dot) with no leave until the next tap elsewhere, which
      // stopped autoplay for good after the first touch or dot click (BIOVAQ, 2026-10-09).
      this.addEventListener('pointerenter', (event) => event.pointerType === 'mouse' && this.hold('hovered', true));
      this.addEventListener('pointerleave', (event) => event.pointerType === 'mouse' && this.hold('hovered', false));
      this.addEventListener('focusin', (event) => this.isKeyboardFocus(event.target) && this.hold('focused', true));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.hold('focused', false);
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.go(this.index + 1);
        if (event.key === 'ArrowLeft') this.go(this.index - 1);
      });
      this.addEventListener('touchstart', (event) => (this.touchX = event.touches[0].clientX), { passive: true });
      this.addEventListener(
        'touchend',
        (event) => {
          const dx = event.changedTouches[0].clientX - (this.touchX ?? event.changedTouches[0].clientX);
          if (Math.abs(dx) > 40) {
            this.swiped = true;
            this.go(this.index + (dx < 0 ? 1 : -1));
          }
        },
        { passive: true }
      );
      // A swipe changes the slide; it must not also open the banner's product.
      this.addEventListener(
        'click',
        (event) => {
          if (this.swiped && event.target.closest('.biovaq-banners__link')) event.preventDefault();
          this.swiped = false;
        },
        true
      );

      this.onVisibility = () => this.schedule();
      document.addEventListener('visibilitychange', this.onVisibility);

      // Theme editor: show the block being edited and stop rotating while it is selected.
      this.onBlockSelect = (event) => {
        if (!this.contains(event.target)) return;
        this.editorHold = true;
        this.go(this.slides.indexOf(event.target));
      };
      this.onBlockDeselect = (event) => {
        if (!this.contains(event.target)) return;
        this.editorHold = false;
        this.schedule();
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.updatePauseButton();
      this.schedule();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    hold(reason, value) {
      this[reason] = value;
      this.schedule();
    }

    isKeyboardFocus(element) {
      try {
        return element.matches(':focus-visible');
      } catch (error) {
        return true; // no :focus-visible support: keep the old pause-on-any-focus behaviour
      }
    }

    schedule() {
      clearTimeout(this.timer);
      if (this.userPaused || this.hovered || this.focused || this.editorHold || document.hidden) return;
      this.timer = setTimeout(() => this.go(this.index + 1), this.interval);
    }

    go(target) {
      const count = this.slides.length;
      const next = ((target % count) + count) % count;
      if (next !== this.index) {
        const current = this.slides[this.index];
        current.classList.remove('is-active');
        current.setAttribute('aria-hidden', 'true');
        current.inert = true;

        const incoming = this.slides[next];
        incoming.classList.add('is-active');
        incoming.removeAttribute('aria-hidden');
        incoming.inert = false;

        this.dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === next);
          if (i === next) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        this.index = next;
      }
      this.schedule();
    }

    updatePauseButton() {
      if (!this.pauseButton) return;
      this.pauseButton.setAttribute('aria-pressed', String(this.userPaused));
      this.pauseButton.querySelector('[data-pause-label]').textContent = this.userPaused
        ? 'Play slideshow'
        : 'Pause slideshow';
    }
  }

  customElements.define('biovaq-banner-slider', BiovaqBannerSlider);
}
